import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';

const useAuthStore = create(
  persist(
    (set, _get) => ({
      user: null,
      token: null,
      isLoading: false,
      error: null,

      login: async (credentials) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/login', credentials);
          const data = response.data;
          if (data.requires_2fa) {
            set({ isLoading: false });
            return { success: true, requires_2fa: true, user_id: data.user_id, user_type: data.user_type };
          }
          const { user, token } = data;
          if (user.user_type !== 'provider') {
            const msg =
              user.user_type === 'admin'
                ? 'Access denied. Please use the Admin login page.'
                : user.user_type === 'delivery'
                  ? 'Access denied. Please use the Delivery login page.'
                  : 'Access denied. Providers only.';
            set({ error: msg, isLoading: false });
            return { success: false, error: msg };
          }
          set({ user, token, isLoading: false });
          // Store token with creation timestamp for expiry tracking (24h expiry)
          localStorage.setItem('token', token);
          localStorage.setItem('token_created_at', Date.now().toString());
          localStorage.setItem('user', JSON.stringify(user));
          return { success: true };
        } catch (error) {
          console.error('Login error:', error);
          let errorMessage = 'Login failed';
          if (error.code === 'ECONNABORTED') {
            errorMessage = 'Connection timeout - is the backend running?';
          } else if (error.message && error.message.includes('Network Error')) {
            errorMessage = 'Cannot connect to server - check if backend is running on port 8000';
          } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
          }
          set({ error: errorMessage, isLoading: false });
          return { success: false, error: errorMessage };
        }
      },

      verify2fa: async (userId, code) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/verify-2fa-login', { user_id: userId, code });
          const data = response.data;
          const { user, token } = data.data || data;
          if (user.user_type !== 'provider') {
            const msg = 'Access denied. Providers only.';
            set({ error: msg, isLoading: false });
            return { success: false, error: msg };
          }
          set({ user, token, isLoading: false });
          localStorage.setItem('token', token);
          localStorage.setItem('token_created_at', Date.now().toString());
          localStorage.setItem('user', JSON.stringify(user));
          return { success: true };
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'Verification failed';
          set({ error: errorMessage, isLoading: false });
          return { success: false, error: errorMessage };
        }
      },

      logout: (sessionExpired = false) => {
        set({ user: null, token: null, error: null });
        localStorage.removeItem('token');
        localStorage.removeItem('token_created_at');
        localStorage.removeItem('user');
        localStorage.removeItem('auth-storage');
        // Use root path since the app is served at subdomain root, not /provider/
        // window.location.href causes a hard reload which re-triggers zustand rehydration
        // Instead, let React Router handle the navigation without full page reload
        if (sessionExpired) {
          window.location.href = '/?session_expired=1';
        } else {
          window.location.href = '/';
        }
      },

      updateProfile: async (userData) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.put('/user/profile', userData);
          const updatedUser = response.data;
          set({ user: updatedUser, isLoading: false });
          return { success: true };
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'Profile update failed';
          set({ error: errorMessage, isLoading: false });
          return { success: false, error: errorMessage };
        }
      },

      checkAuth: async () => {
        const token = localStorage.getItem('token');
        const cachedUser = localStorage.getItem('user');
        if (token && cachedUser) {
          // Proactive token expiry check — tokens expire after 24h
          const tokenCreatedAt = localStorage.getItem('token_created_at');
          if (tokenCreatedAt) {
            const elapsed = Date.now() - parseInt(tokenCreatedAt, 10);
            const TWENTY_THREE_HOURS = 23 * 60 * 60 * 1000;
            if (elapsed > TWENTY_THREE_HOURS) {
              localStorage.removeItem('token');
              localStorage.removeItem('token_created_at');
              localStorage.removeItem('user');
              localStorage.removeItem('auth-storage');
              set({ token: null, user: null });
              return;
            }
          }
          set({ token, user: JSON.parse(cachedUser) });
          // Fetch fresh user data from server
          try {
            const response = await api.get('/user');
            if (response.data?.user) {
              const freshUser = response.data.user;
              set({ user: freshUser });
              localStorage.setItem('user', JSON.stringify(freshUser));
            } else if (response.data) {
              const freshUser = response.data;
              set({ user: freshUser });
              localStorage.setItem('user', JSON.stringify(freshUser));
            }
          } catch (err) {
            // Silently fail — stale cached data is better than no data
          }
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user, token: state.token }),
      onRehydrateStorage: () => (state) => {
        if (state?.token) {
          localStorage.setItem('token', state.token);
          // Restore token_created_at if it was persisted (e.g. from session restore)
          if (!localStorage.getItem('token_created_at')) {
            localStorage.setItem('token_created_at', Date.now().toString());
          }
          if (state.user) {
            localStorage.setItem('user', JSON.stringify(state.user));
          }
        }
      },
    },
  ),
);

export { useAuthStore };
